import {
  Box,
  Button,
  DismissIcon,
  Flex,
  Image,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';

import { useCompare } from './compareProvider';

import styles from './compareBar.module.css';

interface CompareBarProps {
  hidden?: boolean;
}

export const CompareBar = ({ hidden = false }: CompareBarProps) => {
  const {
    enabled,
    products,
    productCount,
    removeProduct,
    clearAll,
    openModal,
    isActive,
  } = useCompare();

  if (!enabled) return null;

  const isVisible = isActive && !hidden;

  let subLabel = '';
  if (productCount < 2) {
    subLabel = 'Minimum of 2';
  }
  if (productCount >= 2 && productCount < 5) {
    subLabel = 'Up to 5';
  }

  return (
    <Box
      className={clsx(styles['bar'], {
        [styles['bar__visible']]: isVisible,
      })}
      role="region"
      aria-label="Product comparison bar"
    >
      <Flex className={styles['thumbs']} alignItems="center" gap={12}>
        {productCount === 0 && (
          <Typography variant="body-semibold" color="text-main">
            Select products to compare
          </Typography>
        )}
        {products.map((product) => (
          <Flex
            key={product.id}
            className={styles['card']}
            alignItems="center"
            gap={12}
          >
            <Box className={styles['cardThumb']}>
              <Image
                src={product.imageUrl}
                altText={product.name}
                width="48px"
                height="48px"
                objectFit="cover"
                borderRadius="rounded-small"
                loading="lazy"
              />
            </Box>
            <Flex flexDirection="column" gap={2} style={{ minWidth: 0 }}>
              <Typography
                variant="small-body-semibold"
                className={styles['cardName']}
              >
                {product.name}
              </Typography>
              {product.brand && (
                <Typography variant="small-body-regular" color="text-main">
                  {product.brand}
                </Typography>
              )}
            </Flex>
            <Button.Icon
              icon={<DismissIcon size="sm" />}
              onClick={() => removeProduct(product.id)}
              aria-label={`Remove ${product.name}`}
              variant="minimal"
            />
          </Flex>
        ))}
      </Flex>
      <Flex className={styles['actions']} alignItems="center" gap={20}>
        {productCount >= 2 && (
          <Button
            variant="secondary"
            label="Clear all"
            onClick={clearAll}
            className={styles['clearAll']}
          />
        )}
        <Button
          variant="primary"
          label={`Compare (${productCount})`}
          subLabel={subLabel}
          onClick={openModal}
          disabled={productCount < 2}
          className={styles['compareButton']}
        />
      </Flex>
    </Box>
  );
};
